import { CdkVirtualScrollViewport, ScrollingModule, VIRTUAL_SCROLL_STRATEGY } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule } from '@ngx-translate/core';

import { ColorCodePipe, ResizeColumnDirective, TableRow } from '@shared/components/data-list/common';
import { DataListBase } from '@shared/components/data-list/data-list.base';
import { TableCellComponent } from '@shared/components/data-list/table-cell/table-cell.component';
import { CipoKeyboardListDirective, VirtualScrollTracker } from '@shared/components/virtual-list';
import { DEFAULT_INFINITE_PAGE } from '@shared/consts';
import { NotFoundComponent } from '@system/not-found/not-found.component';
import { TableVirtualScrollStrategy } from '../common/services/table-virtual-scroll-strategy.service';
import { TableRowActionsComponent } from '../table-row-actions/table-row-actions.component';

@Component({
  selector: 'cipo-infinite-data-list',
  imports: [
    MatTableModule,
    MatSortModule,
    ScrollingModule,
    CommonModule,
    MatMenuModule,
    MatCheckboxModule,
    MatIconModule,
    MatButtonModule,
    TranslateModule,
    FlexLayoutModule,
    TableCellComponent,
    ColorCodePipe,
    NotFoundComponent,
    ResizeColumnDirective,
    TableRowActionsComponent,
    CipoKeyboardListDirective,
  ],
  templateUrl: './infinite-data-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    TableVirtualScrollStrategy,
    { provide: VIRTUAL_SCROLL_STRATEGY, useExisting: TableVirtualScrollStrategy },
  ],
})
export class InfiniteDatalistComponent<T = TableRow> extends DataListBase<T> {
  viewport = viewChild(CdkVirtualScrollViewport);
  table = viewChild('table', { read: ElementRef });

  rowHeight = input<number>(48);
  bufferSize = input<number>(10);

  private readonly _ngZone = inject(NgZone);
  private readonly _scrollStrategy = inject(TableVirtualScrollStrategy);
  private readonly _destroyRef = inject(DestroyRef);

  rows = signal<T[]>([]);
  staticRows: T[] = [];
  lastLoadedPageIndex = 0;

  private readonly scrollTracker = new VirtualScrollTracker();
  selectAllDisabled = computed(() => {
    const rows = this.rows();
    if (!rows?.length) {
      return true;
    }
    // TODO investigate why we need the cast
    return !rows.some(r => ((r as TableRow).selectable ?? true) === true);
  });

  emptyTable = computed(() =>
    Array.from({ length: this.options().pagination?.pageSize || DEFAULT_INFINITE_PAGE }, () => ({ empty: true }) as T),
  );

  /**
   * Skeleton placeholders (while a page loads) and client-injected rows flagged `excludeFromCount`
   * (e.g. the "Data Exports" shortcut) live in `rows` but are not server records, so they must not
   * reach the user's "X of Y" count.
   */
  loadedRowsCount = computed(
    () => this.rows().filter(row => !this.isEmptyRow(row) && !(row as TableRow).excludeFromCount).length,
  );

  private lastTableData: T[] = null;

  constructor() {
    super();
    this._initScrollStrategy();
    // Parent DataListWrapper sets tableData (items) per page; this component appends when pageIndex > 0.
    effect(() => {
      const tableData = this.tableData();
      const options = untracked(this.options);
      if (!tableData) {
        this.viewport()?.scrollToOffset(0);
        this.lastLoadedPageIndex = 0;
        this.loading.set(true);
        this.rows.set(this.emptyTable());
        this.staticRows = [];
        this.lastTableData = null;
        this._scrollStrategy.setVirtualScrollEnabled(false);
        this._scrollStrategy.setDataLength(this.emptyTable().length);
        return;
      }

      if (tableData === this.lastTableData) return;

      this.lastTableData = tableData;

      if (options.pagination.pageIndex === 0) {
        this.lastLoadedPageIndex = 0;
      }

      const newRows = this._stripSkeletonRows(
        options.pagination.pageIndex === 0 ? [...tableData] : this._stripSkeletonRows(this.rows()).concat(tableData),
      );

      this.rows.set(newRows);
      this.staticRows = [...newRows];

      this._scrollStrategy.setVirtualScrollEnabled(true);
      this._scrollStrategy.setDataLength(newRows.length);

      const vp = this.viewport();
      if (vp) {
        this._measureAndUpdateTableStructure();
        vp.checkViewportSize();
      }

      this.checkLeftRightIcons(tableData);
      this.loading.set(false);
    });

    effect(() => {
      if (!this.loading()) {
        untracked(() => {
          if (this.rows().some(r => this.isEmptyRow(r)) && this.tableData() !== undefined) {
            const withoutSkeleton = this._stripSkeletonRows(this.rows());
            if (withoutSkeleton.length !== this.rows().length) {
              this.rows.set(withoutSkeleton);
              this._scrollStrategy.setDataLength(withoutSkeleton.length);
            }
          }
        });
      }
    });

    effect(() => {
      const updatedRows = this.updatedRows();
      if (!updatedRows?.length) {
        return;
      }
      updatedRows.forEach(newRow => {
        const index = untracked(() => this.rows()).findIndex(
          row => row[this.primaryKey()] === newRow[this.primaryKey()],
        );
        if (index !== -1) {
          this.rows.update(rows => {
            rows[index] = newRow;
            return [...rows];
          });
          const selectedRows = this.selection().selected.filter(
            row => row[this.primaryKey()] === newRow[this.primaryKey()],
          );
          if (selectedRows.length) {
            this.selection().deselect(...selectedRows);
          }
        }
      });
    });
  }

  onScrollIndexChange() {
    const viewPort = this.viewport();
    if (!viewPort) return;

    const { scrolledDown, canScroll } = this.scrollTracker.read(viewPort);

    const rows = untracked(() => this.rows());
    const loading = untracked(() => this.loading());

    const { start, end } = viewPort.getRenderedRange();

    if (
      (!scrolledDown && canScroll) ||
      loading ||
      rows.length === 0 ||
      rows.some(r => (r as T & { empty?: boolean }).empty)
    )
      return;

    const pagination = untracked(() => this.options().pagination);
    const nextPage = this.lastLoadedPageIndex + 1;
    const expectedRows = nextPage * (pagination.pageSize || DEFAULT_INFINITE_PAGE);

    if (
      end >= rows.length - 10 &&
      this.lastLoadedPageIndex !== nextPage &&
      rows.length < pagination.length &&
      rows.length >= expectedRows
    ) {
      this.lastLoadedPageIndex = nextPage;
      this.rows.update(r => [...r, { empty: true } as unknown as T]);
      this._scrollStrategy.setDataLength(this.rows().length);
      this.paginationUpdated.emit({ pageIndex: nextPage });
    }
  }

  tableSortChanged(event: Sort) {
    if (this.options()?.serverPaginationSort) {
      this.selection()?.clear();
      this.lastLoadedPageIndex = 0;
      this.viewport()?.scrollToOffset(0);

      this.options.update(options => {
        options.sort = { ...event };
        return options;
      });

      this.sortChanged.emit(event);
    } else {
      this.rows.update(() => {
        if (event.direction === 'asc') {
          return [...this.staticRows].sort((a, b) => (a[event.active] > b[event.active] ? 1 : -1));
        }
        if (event.direction === 'desc') {
          return [...this.staticRows].sort((a, b) => (a[event.active] < b[event.active] ? 1 : -1));
        }
        return [...this.staticRows];
      });
    }
  }

  isAllSelected() {
    const numSelected = this.selection().selected.length;
    const numRows = this.rows().filter(
      d => ((d as TableRow).selectable ?? true) !== false && !(d as T & { empty?: boolean }).empty,
    ).length;
    return numSelected === numRows && numRows > 0;
  }

  toggleAllRows(): void {
    if (this.isAllSelected()) {
      this.selection().clear();
      return;
    }
    // TODO investigate why we need the cast
    this.selection().select(
      ...this.rows().filter(
        d => ((d as TableRow).selectable ?? true) !== false && !(d as T & { empty?: boolean }).empty,
      ),
    );
  }

  trackByRow = (index: number, item: T): unknown => {
    if (this.isEmptyRow(item)) {
      return `skeleton-${index}`;
    }

    const key = this.primaryKey();
    return (item as Record<string, unknown>)[key];
  };

  private isEmptyRow(item: T): item is T & { empty: boolean } {
    return (item as Record<string, unknown>)['empty'] === true;
  }

  private _stripSkeletonRows(rows: T[]): T[] {
    return rows.filter(r => !this.isEmptyRow(r));
  }

  private _initScrollStrategy(): void {
    this._scrollStrategy.setScrollParameters(this.rowHeight(), 0, 0, this.bufferSize());

    afterNextRender(() => {
      this._measureAndUpdateTableStructure();

      const tableEl = this.table()?.nativeElement;
      if (tableEl) {
        this._ngZone.runOutsideAngular(() => {
          const thead = tableEl.querySelector('thead');
          const tfoot = tableEl.querySelector('tfoot');
          const mo = new MutationObserver(() => {
            this._measureAndUpdateTableStructure();
          });

          if (thead) mo.observe(thead, { childList: true, attributes: true });
          if (tfoot) mo.observe(tfoot, { childList: true, attributes: true });

          this._destroyRef.onDestroy(() => mo.disconnect());
        });
      }

      const vp = this.viewport();
      if (vp) {
        this._ngZone.runOutsideAngular(() => {
          const ro = new ResizeObserver(entries => {
            const height = entries[0]?.contentRect.height ?? 0;
            if (height > 0) {
              // checkViewportSize() updates CDK's cached _viewportSize and internally
              // calls onDataLengthChanged(), fixing the race condition where getViewportSize()
              // returns 0 (stale cache) even after the viewport element has a real height.
              vp.checkViewportSize();
            }
          });
          ro.observe(vp.elementRef.nativeElement);
          this._destroyRef.onDestroy(() => ro.disconnect());
        });
      }
    });
  }

  private _measureAndUpdateTableStructure(): void {
    const tableEl = this.table()?.nativeElement;
    if (!tableEl) return;

    const thead = tableEl.querySelector('thead');
    const tfoot = tableEl.querySelector('tfoot');
    const theadHeight = thead?.getBoundingClientRect().height ?? 0;
    const tfootHeight = tfoot?.getBoundingClientRect().height ?? 0;

    this._scrollStrategy.updateHeaderHeight(Math.ceil(theadHeight));
    this._scrollStrategy.updateFooterHeight(Math.ceil(tfootHeight));
  }
}
